import React, { useState, useEffect } from 'react';
import PasswordInput from './PasswordInput';

export default function ManageMembers({ operatorId, onBack }) {
    const [operator, setOperator] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [memberLoading, setMemberLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [newMem, setNewMem] = useState({ username: '', email: '', password: '' });

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    useEffect(() => {
        if (!operatorId) return;
        fetch(`/api/operators/${operatorId}`)
            .then(r => r.json())
            .then(data => setOperator(data))
            .catch(err => console.error('Error fetching operator:', err));

        fetch(`/api/operators/${operatorId}/members`)
            .then(r => r.json())
            .then(data => {
                setMembers(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [operatorId]);

    const handleAddMember = async (e) => {
        e.preventDefault();
        if (!newMem.username || !newMem.email || !newMem.password) return;
        setMemberLoading(true);
        try {
            const res = await fetch(`/api/operators/${operatorId}/members`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newMem)
            });
            if (res.ok) {
                const data = await res.json();
                setMembers(data.members);
                setNewMem({ username: '', email: '', password: '' });
                showToast('Member added successfully');
            }
        } catch (err) {
            showToast('Failed to add member', 'error');
        } finally {
            setMemberLoading(false);
        }
    };

    const handleDeleteMember = async (memberId) => {
        if (!window.confirm('Remove this member?')) return;
        try {
            const res = await fetch(`/api/operators/${operatorId}/members/${memberId}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                const data = await res.json();
                setMembers(data.members);
                showToast('Member removed');
            }
        } catch (err) {
            showToast('Failed to remove member', 'error');
        }
    };

    
    const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
            {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
        </svg>
    );

    const icons = {
        users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 7a4 4 0 100-8 4 4 0 000 8z',
        plus: 'M12 5v14 M5 12h14',
        trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
        mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6',
        lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 00 2 2h14a2 2 0 00 2-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4',
        back: 'M19 12H5M12 19l-7-7 7-7',
        team: 'M17 21v-2a4 4 0 00-3-3.87 M9 7a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
    };

    return (
        <div className="mm-page">
            <style>{`
                .mm-page { padding: 40px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .mm-header { display: flex; align-items: center; gap: 20px; margin-bottom: 32px; }
                .back-btn {
                    width: 40px; height: 40px; border-radius: 50%; background: white; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
                    color: #64748b;
                }
                .back-btn:hover { background: #f1f5f9; color: #1e293b; border-color: #cbd5e1; }
                .mm-title-area h1 { font-size: 24px; font-weight: 700; color: #1e293b; margin: 0; }
                .mm-title-area p { font-size: 14px; color: #64748b; margin: 4px 0 0 0; }

                .mm-content { max-width: 1000px; }

                .mem-form-card {
                    background: white; padding: 32px; border-radius: 16px; border: 1px solid #f1f5f9; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); margin-bottom: 32px;
                }
                .mem-form-title { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 24px; display: flex; align-items: center; gap: 10px; }
                .mem-form-grid { display: grid; grid-template-columns: 1fr 1fr 1fr auto; gap: 20px; align-items: flex-end; }
                .mem-input-group { display: flex; flex-direction: column; gap: 8px; }
                .mem-input-group label { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
                .mem-input-wrapper { position: relative; }
                .mem-input {
                    width: 100%; padding: 10px 12px 10px 38px; border: 1px solid #e2e8f0; border-radius: 10px; 
                    font-size: 14px; outline: none; transition: border-color 0.2s;
                }
                .mem-input:focus { border-color: #3f51b5; }
                .mem-input-wrapper .input-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
                .add-mem-btn {
                    padding: 11px 24px; background: #3f51b5; color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer;
                    transition: all 0.2s; display: flex; align-items: center; gap: 8px;
                }
                .add-mem-btn:hover { background: #303f9f; transform: translateY(-1px); }
                .add-mem-btn:active { transform: translateY(0); }

                .table-container { 
                    background: white; border-radius: 16px; border: 1px solid #f1f5f9; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); overflow: hidden;
                }
                .mem-table { width: 100%; border-collapse: collapse; }
                .mem-table th { background: #f8fafc; padding: 16px 24px; text-align: left; font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; border-bottom: 1px solid #f1f5f9; }
                .mem-table td { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #1e293b; }
                .mem-table tr:last-child td { border-bottom: none; }
                .mem-table tr:hover { background: #fcfdfe; }

                .action-btn-del {
                    background: #fef2f2; color: #ef4444; border: none; width: 32px; height: 32px; border-radius: 8px;
                    display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
                }
                .action-btn-del:hover { background: #fee2e2; color: #dc2626; }

                .op-toast {
                    position: fixed; top: 20px; right: 20px; padding: 12px 24px; border-radius: 8px;
                    background: #333; color: white; z-index: 1000; animation: slideIn 0.3s ease-out;
                }
                .op-toast.success { background: #10b981; }
                .op-toast.error { background: #ef4444; }
                @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

                /* Dark Mode Support */
                .theme-dark .mm-page { background: #0f0f0f; }
                .theme-dark .mm-title-area h1 { color: #fff; }
                .theme-dark .mm-title-area p { color: #888; }
                .theme-dark .back-btn { background: #1a1a1a; border-color: #242424; color: #888; }
                .theme-dark .mem-form-card, .theme-dark .table-container { background: #161616; border-color: #242424; }
                .theme-dark .mem-form-title { color: #fff; }
                .theme-dark .mem-input { background: #1e1e1e; border-color: #2a2a2a; color: #eee; }
                .theme-dark .mem-table th { background: #1e1e1e; color: #888; border-bottom-color: #242424; }
                .theme-dark .mem-table td { color: #ccc; border-bottom-color: #222; }
                .theme-dark .mem-table tr:hover { background: #1a1a1a; }
                .theme-dark .action-btn-del { background: rgba(239, 68, 68, 0.1); }
            `}</style>

            {toast && <div className={`op-toast ${toast.type}`}>{toast.msg}</div>}

            <div className="mm-header">
                <button className="back-btn" onClick={onBack} title="Go Back">
                    <Icon d={icons.back} size={18} />
                </button>
                <div className="mm-title-area">
                    <h1>Manage Members</h1>
                    <p>{operator ? `Operator: ${operator.companyName}` : 'Loading operator...'}</p>
                </div>
            </div>

            <div className="mm-content">
                <div className="mem-form-card">
                    <div className="mem-form-title">
                        <Icon d={icons.team} size={18} color="#3f51b5" />
                        Add New Member
                    </div>
                    <form onSubmit={handleAddMember} className="mem-form-grid">
                        <div className="mem-input-group">
                            <label>Username</label>
                            <div className="mem-input-wrapper">
                                <Icon d={icons.users} size={14} className="input-icon" />
                                <input
                                    className="mem-input" placeholder="Enter username"
                                    value={newMem.username} onChange={e => setNewMem({ ...newMem, username: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mem-input-group">
                            <label>Email Address</label>
                            <div className="mem-input-wrapper">
                                <Icon d={icons.mail} size={14} className="input-icon" />
                                <input
                                    className="mem-input" placeholder="Enter email"
                                    value={newMem.email} onChange={e => setNewMem({ ...newMem, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mem-input-group">
                            <label>Password</label>
                            <div className="mem-input-wrapper">
                                <Icon d={icons.lock} size={14} className="input-icon" />
                                <PasswordInput
                                    className="mem-input" placeholder="Enter password"
                                    value={newMem.password} onChange={e => setNewMem({ ...newMem, password: e.target.value })}
                                />
                            </div>
                        </div>
                        <button type="submit" className="add-mem-btn" disabled={memberLoading}>
                            {memberLoading ? 'Adding...' : <><Icon d={icons.plus} size={14} strokeWidth={3} /> Add Member</>}
                        </button>
                    </form>
                </div>

                <div className="table-container">
                    <table className="mem-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Password</th>
                                <th style={{ textAlign: 'right' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px' }}>Loading members...</td></tr>
                            ) : members.length === 0 ? (
                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No members found for this operator.</td></tr>
                            ) : (
                                members.map(m => (
                                    <tr key={m._id}>
                                        <td style={{ fontWeight: 600 }}>{m.firstName}</td>
                                        <td>{m.email}</td>
                                        <td style={{ fontFamily: 'monospace', color: '#94a3b8' }}>••••••••</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button className="action-btn-del" onClick={() => handleDeleteMember(m._id)} title="Delete Member">
                                                <Icon d={icons.trash} size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
